import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  CreditCard,
  MapPin,
  CalendarIcon,
  Clock,
  Users,
  Compass,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PaymentMethodCards from "@/components/PaymentMethodCards";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

interface TourBookingState {
  tourId: string;
  tourSlug: string;
  tourTitle: string;
  tourImage: string | null;
  destinationTitle: string;
  pricingMode: string;
  adultPrice: number | null;
  childPrice: number | null;
  adults: number;
  children: number;
  selectedPackageId: string | null;
  selectedPackageName: string | null;
  selectedPackagePrice: number | null;
  excursionDate: string;
  schedule: string | null;
  totalPrice: number;
  duration: string | null;
}

const TourBookingPage = () => {
  useSeo({ robots: "noindex,follow", title: "Complete Booking | Allen Travels RD" });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as TourBookingState | null;

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pickupPoint, setPickupPoint] = useState("");
  const [comments, setComments] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeMethods, setActiveMethods] = useState<{ method: string }[]>([]);

  useEffect(() => {
    supabase
      .from("payment_methods")
      .select("method, is_active")
      .eq("is_active", true)
      .then(({ data }) => {
        const methods = (data as any[]) ?? [];
        setActiveMethods(methods);
        if (methods.length > 0 && !paymentMethod) {
          setPaymentMethod(methods[0].method);
        }
      });
  }, []);

  if (!state) {
    return (
      <div className="min-h-screen bg-gradient-soft flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-5 max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-ocean mx-auto flex items-center justify-center">
              <Compass className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              No booking in progress
            </h1>
            <p className="text-muted-foreground">
              Pick a tour first and we'll guide you through the booking flow.
            </p>
            <Button size="lg" onClick={() => navigate("/tours")} className="bg-gradient-ocean">
              Browse Tours
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!fullName || !email || !phone) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);

    const { data: booking, error } = await supabase.from("tour_bookings").insert({
      tour_id: state.tourId,
      tour_package_id: state.selectedPackageId || null,
      excursion_date: format(new Date(state.excursionDate), "yyyy-MM-dd"),
      schedule: state.schedule,
      adults: state.adults,
      children: state.children,
      pricing_mode: state.pricingMode,
      total_price: state.totalPrice,
      customer_name: fullName,
      customer_email: email,
      customer_phone: phone,
      pickup_point: pickupPoint || null,
      comments: comments || null,
      payment_method: paymentMethod,
      payment_status: paymentMethod === "cash" ? "unpaid" : "pending",
    } as any).select().single();

    setSubmitting(false);

    if (error) {
      toast.error("Failed to create booking. Please try again.");
      console.error(error);
      return;
    }

    if (paymentMethod !== "paypal") {
      supabase.functions.invoke("send-tour-booking-email", {
        body: { booking_id: booking.id },
      }).catch((err) => console.error("Tour email send error:", err));
    }

    if (paymentMethod === "paypal") {
      navigate("/payment", {
        state: {
          type: "tour",
          bookingId: booking.id,
          bookingCode: booking.booking_code,
          tourTitle: state.tourTitle,
          tourImage: state.tourImage,
          destinationTitle: state.destinationTitle,
          excursionDate: state.excursionDate,
          schedule: state.schedule,
          adults: state.adults,
          children: state.children,
          pricingMode: state.pricingMode,
          selectedPackageName: state.selectedPackageName,
          totalPrice: state.totalPrice,
          customerName: fullName,
          customerEmail: email,
          customerPhone: phone,
          pickupPoint,
          comments,
          paymentMethod,
        },
      });
    } else {
      toast.success("Booking confirmed!");
      navigate("/tour-confirmation", {
        state: {
          booking,
          tourTitle: state.tourTitle,
          tourImage: state.tourImage,
          destinationTitle: state.destinationTitle,
          duration: state.duration,
          pricingMode: state.pricingMode,
          selectedPackageName: state.selectedPackageName,
          adultPrice: state.adultPrice,
          childPrice: state.childPrice,
        },
      });
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  };

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <Navbar />

      {/* Hero strip */}
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-hero" />
        <div className="absolute inset-0 -z-10 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,white,transparent_60%)]" />
        <div className="container mx-auto px-4 text-white">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Back to tour
          </button>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5 text-accent" /> Final Step
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-semibold mt-4 leading-tight">
            Confirm your <span className="text-gradient-sunset">Caribbean experience</span>
          </h1>
          <p className="mt-3 text-white/75 max-w-xl">
            Tell us who's coming and how you'd like to pay. We'll handle the rest.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 -mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <motion.div
              {...fadeUp}
              className="p-7 rounded-2xl glass shadow-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-ocean flex items-center justify-center shadow-glow">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">
                    Personal Information
                  </h2>
                  <p className="text-xs text-muted-foreground">We'll send your voucher here</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-11 bg-background/70"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 bg-background/70"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    placeholder="+1 809 555 1234"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-11 bg-background/70"
                  />
                </div>
              </div>
            </motion.div>

            {/* Pickup & Comments */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              className="p-7 rounded-2xl glass shadow-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-ocean flex items-center justify-center shadow-glow">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">
                    Pickup &amp; Notes
                  </h2>
                  <p className="text-xs text-muted-foreground">Where should we collect you?</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Pickup Point (Hotel / Address)</Label>
                  <Input
                    placeholder="e.g. Hard Rock Hotel, Punta Cana"
                    value={pickupPoint}
                    onChange={(e) => setPickupPoint(e.target.value)}
                    className="h-11 bg-background/70"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Comments / Special Requests</Label>
                  <Textarea
                    placeholder="Allergies, celebration, accessibility…"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="bg-background/70 min-h-[110px]"
                  />
                </div>
              </div>
            </motion.div>

            {/* Payment Method */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.16 }}
              className="p-7 rounded-2xl glass shadow-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-sunset flex items-center justify-center shadow-glow">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-foreground">
                    Payment Method
                  </h2>
                  <p className="text-xs text-muted-foreground">Pay now or at meeting point</p>
                </div>
              </div>
              <PaymentMethodCards
                value={paymentMethod}
                onChange={setPaymentMethod}
                activeMethods={activeMethods}
                cashSubtitle="Pay at the meeting point"
              />

              <Button
                size="lg"
                className="w-full font-semibold text-base mt-6 h-12 bg-gradient-ocean hover:opacity-95 shadow-glow"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Processing…" : `Confirm Booking · $${state.totalPrice}`}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                Secure checkout · Free cancellation up to 24h before
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Summary */}
          <div className="lg:col-span-1">
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="sticky top-28 rounded-2xl overflow-hidden glass shadow-elegant"
            >
              {state.tourImage && (
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={state.tourImage}
                    alt={state.tourTitle}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/85 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <h4 className="font-display font-semibold text-lg leading-tight">
                      {state.tourTitle}
                    </h4>
                    {state.destinationTitle && (
                      <p className="text-xs text-white/80 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {state.destinationTitle}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="p-6 space-y-5">
                {!state.tourImage && (
                  <div>
                    <h4 className="font-display font-semibold text-foreground">{state.tourTitle}</h4>
                    {state.destinationTitle && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {state.destinationTitle}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  <SummaryRow
                    icon={<CalendarIcon className="w-4 h-4" />}
                    label="Date"
                    value={format(new Date(state.excursionDate), "PPP")}
                  />
                  {state.schedule && (
                    <SummaryRow
                      icon={<Clock className="w-4 h-4" />}
                      label="Schedule"
                      value={state.schedule}
                    />
                  )}
                  {state.duration && (
                    <SummaryRow
                      icon={<Clock className="w-4 h-4" />}
                      label="Duration"
                      value={state.duration}
                    />
                  )}

                  {state.pricingMode === "fixed" ? (
                    <>
                      <SummaryRow
                        icon={<Users className="w-4 h-4" />}
                        label="Adults"
                        value={`${state.adults} × $${state.adultPrice}`}
                      />
                      {state.children > 0 && (
                        <SummaryRow
                          icon={<Users className="w-4 h-4" />}
                          label="Children"
                          value={`${state.children} × $${state.childPrice}`}
                        />
                      )}
                    </>
                  ) : (
                    state.selectedPackageName && (
                      <SummaryRow
                        icon={<Compass className="w-4 h-4" />}
                        label="Package"
                        value={state.selectedPackageName}
                      />
                    )
                  )}
                </div>

                <div className="pt-5 border-t border-border/60">
                  <div className="flex items-end justify-between">
                    <p className="text-muted-foreground text-sm">Total</p>
                    <p className="text-3xl font-display font-semibold text-gradient-ocean">
                      ${state.totalPrice}
                    </p>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 text-right">
                    Taxes &amp; fees included
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const SummaryRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground flex items-center gap-1.5">
      {icon} {label}
    </span>
    <span className="font-medium text-foreground text-right">{value}</span>
  </div>
);

export default TourBookingPage;
